// Job Postings & Hiring articles for the Help Center.

const jobPostings = {
  slug: 'job-postings',
  label: 'Job Postings & Hiring',
  icon: 'PiFileText',
  description: 'Post a project, review applicants, and hire someone with a contract.',
  articles: [
    {
      slug: 'posting-a-job',
      title: 'Posting a job',
      summary: 'Describe the work and let freelancers apply, instead of buying a fixed package.',
      body: [
        {
          type: 'p',
          text: 'A job posting is a good fit when your project does not match a fixed package, or when you want to see who is interested before committing to anyone. Go to Post a Job, describe the work, and choose a budget type: a fixed price, an hourly rate, or an open, negotiable budget.',
        },
        {
          type: 'image',
          src: '/images/help/job-postings/post-a-job-form.png', width: 1200, height: 615,
          alt: 'The Post a Job form, with title, category, description, and budget type fields',
          caption: 'The Post a Job form',
        },
        {
          type: 'steps',
          items: [
            'Go to Post a Job.',
            'Add a title, category, and a clear description of the work.',
            'Choose fixed, hourly, or negotiable as your budget type.',
            'Set a budget amount and publish the posting.',
          ],
        },
      ],
    },
    {
      slug: 'how-freelancers-apply',
      title: 'How freelancers apply',
      summary: 'Applicants send a proposal you can review before responding to anyone.',
      body: [
        {
          type: 'p',
          text: 'Once your job is live, freelancers can apply with a short proposal. You can review every applicant from the Applicants page for that posting, see their profile, and message anyone you want to ask questions before deciding.',
        },
        {
          type: 'p',
          text: 'For a negotiable-budget posting, a freelancer can apply with a few different package tiers instead of one flat proposal, each with its own price, delivery time, and revisions, so you can see the range of what they would charge for different scopes.',
        },
        {
          type: 'p',
          text: 'There is no rush to respond immediately. Take the time to compare applicants, and message a few of them if it helps you decide who is the best fit.',
        },
      ],
    },
    {
      slug: 'writing-your-proposal-with-ai',
      title: 'Writing your proposal with AI',
      summary: 'Draft your pitch to a client from a prompt, grounded in the actual posting and your profile.',
      body: [
        {
          type: 'p',
          text: 'When you apply to a job posting, look for Write with AI next to your proposal message. It drafts a pitch that references something specific from the posting and connects it to your real background, not a generic template.',
        },
        {
          type: 'p',
          text: 'Like everywhere else this shows up, it only produces a draft for you to review and edit. Nothing is sent until you submit your application yourself.',
        },
        {
          type: 'note',
          text: 'It never invents a price or a rate. Your proposal terms are always set separately, on their own form.',
        },
      ],
    },
    {
      slug: 'getting-an-applicant-summary',
      title: 'Getting an applicant summary',
      summary: 'A short AI summary of your strongest applicants once you have a few to compare.',
      body: [
        {
          type: 'p',
          text: 'Once at least two people have applied to your posting, an At a glance summary appears above your applicant list. It names who seems like the strongest fit based on their real skills and experience, and says plainly if anyone looks like a mismatch.',
        },
        {
          type: 'p',
          text: 'It is a read only summary. Nobody is hidden or reordered because of it, it just gives you a starting point before you read through applicants yourself.',
        },
      ],
    },
    {
      slug: 'hiring-someone',
      title: 'Hiring someone',
      summary: 'Send a contract offer, which the freelancer reviews and signs to accept.',
      body: [
        {
          type: 'p',
          text: 'When you are ready to hire an applicant, you send them a contract offer with the agreed terms: scope of work, deliverables, timeline, and revision policy. You sign your side when you send it. The freelancer then reviews it and either accepts and signs their own side, declines, or asks you to change something first.',
        },
        {
          type: 'p',
          text: 'Once the freelancer accepts, the contract becomes active and the other applicants are automatically notified the position has been filled.',
        },
      ],
    },
    {
      slug: 'fixed-price-vs-hourly-contracts',
      title: 'Fixed price versus hourly contracts',
      summary: 'One is a set amount for the whole project, the other is billed per hour worked.',
      body: [
        {
          type: 'p',
          text: 'A fixed price contract has one agreed total for the entire project. You fund that amount into escrow once the contract is active, and it is released to the freelancer once you approve the finished work.',
        },
        {
          type: 'p',
          text: 'An hourly contract has no upfront charge. The freelancer logs their hours as they work, submits an invoice, usually on a weekly basis, and you review and approve each invoice before it is charged. You only ever pay for hours you have actually approved.',
        },
      ],
    },
    {
      slug: 'how-escrow-works-on-contracts',
      title: 'How escrow works on contracts',
      summary: 'Money is held safely and only released once you approve the work.',
      body: [
        {
          type: 'p',
          text: 'On a fixed price contract, once it is active, you fund the agreed amount. That money is held in escrow, not sent to the freelancer, while the work is in progress. When the freelancer delivers and you approve it, the funds are released to them.',
        },
        {
          type: 'p',
          text: 'If a contract is cancelled while funds are still held, the client is refunded in full. This protects both sides: the client is not paying blind, and the freelancer knows the money for the project is already set aside.',
        },
      ],
    },
    {
      slug: 'logging-and-approving-hours',
      title: 'Logging and approving hours',
      summary: 'Freelancers submit weekly invoices, clients review and approve them.',
      body: [
        {
          type: 'p',
          text: 'On an hourly contract, the freelancer tracks their time and submits an invoice for the hours worked, usually once a week. The client reviews it in their conversation with the freelancer and can approve it, which charges the agreed rate for those hours, or dispute it if something looks wrong.',
        },
        {
          type: 'p',
          text: 'Because nothing is charged automatically, it helps to check in regularly rather than letting several weeks of invoices pile up unreviewed.',
        },
      ],
    },
    {
      slug: 'changing-contract-terms-or-resolving-a-disagreement',
      title: 'Changing contract terms or resolving a disagreement',
      summary: 'Propose an amendment for a scope change, or open a review if you disagree about delivered work.',
      body: [
        {
          type: 'p',
          text: 'If the scope, timeline, or revisions on an active contract need to change, either the client or the freelancer can propose an amendment describing the new terms. The other side accepts or declines it, and the contract only changes once they accept.',
        },
        {
          type: 'p',
          text: 'If you and the other side disagree about whether delivered work meets the contract, either of you can open a review instead of letting it stall. This holds the contract\'s funds until it is resolved, either by releasing payment to the freelancer, refunding the client, or splitting it between you.',
        },
      ],
    },
  ],
};

export default jobPostings;
